import { wizardService } from './wizardService';
import type { ReprovePayload } from '../types/reprove';

export async function saveReport(
  payload: ReprovePayload,
  filterState: {
    representante: string;
    ciudad: string;
    puntoVenta: string;
    date: string;
    vendedora: boolean;
    nombreVendedora: string;
    cuotaPiezas: number | '';
    ventaPromedio: number | '';
  }
): Promise<{
  success: boolean;
  reportId?: string;
  error?: string;
}> {
  try {
    // Validaciones básicas
    if (
      !filterState.representante ||
      !filterState.ciudad ||
      !filterState.puntoVenta
    ) {
      throw new Error('Faltan datos obligatorios para guardar el reporte');
    }

    const scorePct = payload.scorePct;

    if (
      typeof scorePct !== 'number' ||
      isNaN(scorePct) ||
      !isFinite(scorePct)
    ) {
      console.error('❌ scorePct inválido:', scorePct);
      throw new Error(
        `scorePct inválido: ${scorePct}. Debe ser un número válido entre 0 y 100.`
      );
    }

    if (scorePct < 0 || scorePct > 100) {
      console.error('❌ scorePct fuera de rango:', scorePct);
      throw new Error(
        `scorePct fuera de rango: ${scorePct}. Debe estar entre 0 y 100.`
      );
    }

    const apiPayload = {
      representativeId: filterState.representante,
      cityId: filterState.ciudad,
      pointId: filterState.puntoVenta,
      date: filterState.date,
      hasSeller: filterState.vendedora,
      sellerId: filterState.vendedora ? filterState.nombreVendedora : undefined,
      otcValue:
        typeof filterState.cuotaPiezas === 'number'
          ? filterState.cuotaPiezas
          : undefined,
      sellerMetric:
        typeof filterState.ventaPromedio === 'number'
          ? filterState.ventaPromedio
          : undefined,
      answers: payload.answers,
      comments: payload.meta.comments ?? undefined,
      scorePct: Number(scorePct.toFixed(2)),
    };

    const cleanPayload = Object.fromEntries(
      Object.entries(apiPayload).filter(([_, value]) => value !== undefined)
    );

    // Llamar a la API
    const savedReport = await wizardService.reports.createReport(cleanPayload);

    return {
      success: true,
      reportId: savedReport.id,
    };
  } catch (error) {
    const errorMessage =
      error instanceof Error ? error.message : 'Error desconocido';

    return {
      success: false,
      error: errorMessage,
    };
  }
}

function validateIds(filterState: any): string[] {
  const errors: string[] = [];
  const CUID_REGEX = /^c[a-z0-9]{25}$/;

  if (!CUID_REGEX.test(filterState.representante)) {
    errors.push(`representativeId inválido: ${filterState.representante}`);
  }

  if (!CUID_REGEX.test(filterState.ciudad)) {
    errors.push(`cityId inválido: ${filterState.ciudad}`);
  }

  if (!CUID_REGEX.test(filterState.puntoVenta)) {
    errors.push(`pointId inválido: ${filterState.puntoVenta}`);
  }

  if (
    filterState.vendedora &&
    filterState.nombreVendedora &&
    !CUID_REGEX.test(filterState.nombreVendedora)
  ) {
    errors.push(`sellerId inválido: ${filterState.nombreVendedora}`);
  }

  return errors;
}
export async function saveReportWithValidation(
  payload: ReprovePayload,
  filterState: any
): Promise<{
  success: boolean;
  reportId?: string;
  error?: string;
}> {
  // Pre-validar IDs
  const validationErrors = validateIds(filterState);
  if (validationErrors.length > 0) {
    return {
      success: false,
      error: 'IDs inválidos: ' + validationErrors.join(', '),
    };
  }

  // Si pasa la validación, proceder normalmente
  return saveReport(payload, filterState);
}

// ✅ FUNCIÓN PARA DEBUGGING: Ver el estado actual de los datos
export function debugReportData(payload: ReprovePayload, filterState: any) {
  // Object.entries(payload.answers).forEach(([tabIndex, tabAnswers]) => {
  //   const answerCount = Object.keys(tabAnswers || {}).length;
  //   const trueCount = Object.values(tabAnswers || {}).filter(
  //     (v) => v === true
  //   ).length;
  //   const falseCount = Object.values(tabAnswers || {}).filter(
  //     (v) => v === false
  //   ).length;
  // });

  // Verificar IDs
  const CUID_REGEX = /^c[a-z0-9]{25}$/;

  const ids = {
    representante: filterState.representante,
    ciudad: filterState.ciudad,
    puntoVenta: filterState.puntoVenta,
    vendedora: filterState.nombreVendedora,
  };

  Object.entries(ids).forEach(([name, id]) => {
    if (id) {
      const isValid = CUID_REGEX.test(id);
      console.log(
        `   ${name}: ${id.substring(0, 15)}... ${isValid ? '✅' : '❌'}`
      );
    } else {
      console.log(`   ${name}: (vacío)`);
    }
  });
}

export default saveReport;
